import React, { useRef, useState, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { X, Camera, RotateCcw, SwitchCamera, Loader2, AlertTriangle, Smartphone, FileText, RefreshCw, Clock, MapPin, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useAttendanceGate } from '@/context/AttendanceContext';

// Import shared components (camera, GPS, photo quality — kept as-is)
import { CameraOverlay } from './CameraOverlay';
import { CountdownTimer } from './CountdownTimer';
import { GPSIndicator } from './GPSIndicator';
import { SuccessCelebration } from './SuccessCelebration';
import { PhotoQualityIndicator } from './PhotoQualityIndicator';
import { addGpsWatermark } from '@/utils/photoWatermark';

// Warning thresholds (matching backend constants)
const GPS_ACCURACY_WARNING_THRESHOLD = 100; // meters

// GPS timeout: 30 seconds before offering manual fallback (REBUILD-09)
const GPS_TIMEOUT_MS = 30000;

// Helper: format time in IST
function formatTimeIST(date) {
  return date.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
}

// Helper: check if current IST time is before 1 PM
function isBeforeOnePM() {
  const now = new Date();
  const istHour = parseInt(
    now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata', hour: 'numeric', hour12: false })
  );
  return istHour < 13;
}

export default function CheckInModal() {
  const {
    showCheckInModal,
    setShowCheckInModal,
    handleCheckInSuccess,
    hasPendingMissedCheckout
  } = useAttendanceGate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  // State
  const [capturedImage, setCapturedImage] = useState(null);
  const [retakeCount, setRetakeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = front, 'environment' = back
  const [showCountdown, setShowCountdown] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsError, setGpsError] = useState(false);
  const [step, setStep] = useState('info'); // 'info', 'camera', 'preview', 'submitting', 'success'
  const [submitError, setSubmitError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Warning states (EDGE-02)
  const [gpsWarning, setGpsWarning] = useState(null);
  const [distanceWarning, setDistanceWarning] = useState(null);

  // Photo fallback states (EDGE-03)
  const [photoError, setPhotoError] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(null); // 'phone', 'text'
  const [fallbackReason, setFallbackReason] = useState('');
  const [uploadedPhoto, setUploadedPhoto] = useState(null);

  // GPS timeout state (REBUILD-09)
  const [gpsTimedOut, setGpsTimedOut] = useState(false);
  const [gpsTimeRemaining, setGpsTimeRemaining] = useState(30);
  const gpsTimerRef = useRef(null);
  const gpsCountdownRef = useRef(null);

  // --- Free-flow step 1 state ---
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationTab, setLocationTab] = useState('home');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullDay, setIsFullDay] = useState(isBeforeOnePM());

  const MAX_RETAKES = 3;

  // Fetch available locations when modal opens
  useEffect(() => {
    if (!showCheckInModal) return;
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const response = await axiosInstance.get('/attendance/daily/locations');
        setLocations(response.data?.locations || response.data || []);
      } catch (error) {
        console.error('[CheckInModal] Failed to fetch locations:', error);
        toast.error('Failed to load locations. Please try again.');
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [showCheckInModal]);

  // Live clock — update every second
  useEffect(() => {
    if (!showCheckInModal || step !== 'info') return;
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      setIsFullDay(isBeforeOnePM());
    }, 1000);
    return () => clearInterval(timer);
  }, [showCheckInModal, step]);

  // Video constraints
  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 }
  };

  // Toggle camera
  const toggleCamera = useCallback(() => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, []);

  // Start countdown before capture
  const startCapture = () => {
    setShowCountdown(true);
  };

  // Called when countdown completes — captures photo and adds GPS watermark
  const handleCountdownComplete = async () => {
    setShowCountdown(false);
    if (webcamRef.current) {
      const rawImage = webcamRef.current.getScreenshot();
      if (rawImage) {
        try {
          const watermarked = await addGpsWatermark(rawImage, {
            locationName: selectedLocation?.location_name || selectedLocation?.name || 'Unknown',
            locationType: selectedLocation?.location_type || 'gym',
            latitude: gpsLocation?.latitude || null,
            longitude: gpsLocation?.longitude || null,
            accuracy: gpsLocation?.accuracy || null,
            type: 'check-in',
          });
          setCapturedImage(watermarked);
        } catch {
          setCapturedImage(rawImage); // fallback to raw if watermark fails
        }
        setStep('preview');
      } else {
        toast.error('Failed to capture photo. Please try again.');
      }
    }
  };

  // Retake photo
  const retake = () => {
    if (retakeCount >= MAX_RETAKES) {
      toast.error('Maximum retakes reached. Please proceed with current photo.');
      return;
    }
    setCapturedImage(null);
    setRetakeCount(prev => prev + 1);
    setStep('camera');
  };

  // GPS location update handler with accuracy warning check (EDGE-02)
  const handleLocationUpdate = useCallback((location) => {
    setGpsLocation(location);
    setGpsError(false);

    // GPS acquired — clear timeout timers (REBUILD-09)
    clearTimeout(gpsTimerRef.current);
    clearInterval(gpsCountdownRef.current);
    setGpsTimedOut(false);

    // Check accuracy warning (>100m)
    if (location.accuracy > GPS_ACCURACY_WARNING_THRESHOLD) {
      setGpsWarning({
        type: 'poor_accuracy',
        message: `GPS accuracy is ${Math.round(location.accuracy)}m. Check-in will be flagged for review.`
      });
    } else {
      setGpsWarning(null);
    }
  }, []);

  // Start GPS timeout when modal opens and GPS is not yet acquired (REBUILD-09)
  useEffect(() => {
    if (!showCheckInModal || gpsLocation) return;

    // Start 30-second timeout
    setGpsTimeRemaining(30);
    setGpsTimedOut(false);

    gpsTimerRef.current = setTimeout(() => {
      setGpsTimedOut(true);
    }, GPS_TIMEOUT_MS);

    gpsCountdownRef.current = setInterval(() => {
      setGpsTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(gpsCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(gpsTimerRef.current);
      clearInterval(gpsCountdownRef.current);
    };
  }, [showCheckInModal, gpsLocation]);

  // Handle webcam error - show fallback options (EDGE-03)
  const handleWebcamError = useCallback((error) => {
    console.error('Webcam error:', error);
    setPhotoError(true);
    setFallbackMode('phone');
  }, []);

  // Handle file upload from phone camera fallback
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedPhoto(e.target.result);
        setCapturedImage(e.target.result);
        setFallbackMode(null);
        setStep('preview');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Proceed to camera
  const proceedToCamera = () => {
    if (!selectedLocation) {
      toast.error('Please select a gym location');
      return;
    }
    if (!gpsLocation) {
      toast.error('GPS location required. Move outdoors or near a window for better signal.');
      return;
    }
    setStep('camera');
  };

  // Submit check-in with fallback support
  const handleSubmit = async () => {
    // Check for either captured image or fallback reason
    if (!capturedImage && !fallbackReason) {
      toast.error('Please take a photo or provide a reason for no photo');
      return;
    }

    if (!gpsLocation) {
      toast.error('GPS location required. Please enable location services and try again.');
      return;
    }

    if (!selectedLocation) {
      toast.error('No location selected.');
      return;
    }

    setLoading(true);
    setStep('submitting');

    try {
      const formData = new FormData();
      formData.append('location_id', selectedLocation.location_id || selectedLocation._id || selectedLocation.id);
      formData.append('location_name', selectedLocation.location_name || selectedLocation.name);
      formData.append('location_type', selectedLocation.location_type || selectedLocation.type || 'gym');
      formData.append('is_home_gym', selectedLocation.is_home_gym !== false ? 'true' : 'false');

      // GPS coordinates are always required
      formData.append('latitude', gpsLocation.latitude);
      formData.append('longitude', gpsLocation.longitude);
      formData.append('accuracy', gpsLocation.accuracy);

      // Add photo or fallback reason
      if (capturedImage) {
        const blob = await fetch(capturedImage).then(r => r.blob());
        formData.append('photo', blob, 'checkin.jpg');
      } else if (fallbackReason) {
        // Text fallback: no photo, just reason
        formData.append('photo_fallback_reason', fallbackReason);
      }

      setUploadProgress(0);
      const response = await axiosInstance.post('/attendance/daily/check-in', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(percent);
        }
      });

      // Show warning if flagged
      if (response.data?.flagged_for_review) {
        toast.warning(response.data.warning || 'Check-in flagged for review');
      }

      // Show celebration
      setSubmitError(false);
      setShowCelebration(true);
      setStep('success');
    } catch (error) {
      // Network error (no server response)
      if (!error.response) {
        toast.error('Network error. Please check your internet connection and try again.', { duration: 15000 });
        setSubmitError(true);
        setStep('preview');
        setLoading(false);
        return;
      }

      setStep('preview');

      // Handle specific GPS enforcement error codes from backend
      const errorData = error.response?.data;
      const errorCode = errorData?.error_code;

      if (errorCode === 'outside_geofence') {
        const dist = Math.round(errorData.distance_meters);
        const max = errorData.required_radius;
        toast.error(`You are ${dist}m from the gym (max ${max}m). Move closer and try again.`, { duration: 6000 });
      } else if (errorCode === 'gps_accuracy_too_poor') {
        const acc = Math.round(errorData.accuracy_meters);
        toast.error(`GPS signal too weak (${acc}m accuracy). Step outside or near a window.`, { duration: 6000 });
      } else if (errorCode === 'gps_required') {
        toast.error('GPS location is required. Enable location services in your browser settings.', { duration: 6000 });
      } else if (errorCode === 'unconfigured_location') {
        toast.error('This gym location is not set up for GPS check-in. Contact your admin.', { duration: 6000 });
      } else if (errorData?.detail) {
        const detail = errorData.detail;
        const message = typeof detail === 'object' ? detail.message : detail;
        const action = typeof detail === 'object' ? detail.action : null;
        toast.error(action ? `${message} ${action}` : message, { duration: 15000 });
      } else {
        toast.error(errorData?.message || 'Check-in failed. Please try again.', { duration: 4000 });
      }
      setSubmitError(true);
    } finally {
      setLoading(false);
    }
  };

  // Submit with text fallback reason
  const handleTextFallbackSubmit = async () => {
    if (!fallbackReason.trim()) {
      toast.error('Please provide a reason for no photo');
      return;
    }
    await handleSubmit();
  };

  // Handle celebration complete
  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    handleCheckInSuccess();
  };

  // Close modal
  const handleClose = () => {
    // Reset all state
    setCapturedImage(null);
    setRetakeCount(0);
    setShowCountdown(false);
    setStep('info');
    setGpsWarning(null);
    setDistanceWarning(null);
    setPhotoError(false);
    setFallbackMode(null);
    setFallbackReason('');
    setUploadedPhoto(null);
    setSelectedLocation(null);
    setLocations([]);
    // Reset GPS timeout state (REBUILD-09)
    setGpsTimedOut(false);
    setGpsTimeRemaining(30);
    clearTimeout(gpsTimerRef.current);
    clearInterval(gpsCountdownRef.current);
    setShowCheckInModal(false);
  };

  if (!showCheckInModal) return null;

  // If there is a pending missed checkout, don't allow check-in — show message
  if (hasPendingMissedCheckout) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Pending Checkout</h2>
            <button onClick={() => setShowCheckInModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={20} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
              <AlertTriangle size={24} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">You have a missed checkout</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  Your previous check-in was not closed. Please resolve it before checking in again.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCheckInModal(false)}
              className="w-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show celebration overlay
  if (showCelebration) {
    return (
      <SuccessCelebration
        title="You're Checked In!"
        subtitle={`Welcome to ${selectedLocation?.location_name || selectedLocation?.name || 'your gym'}`}
        onComplete={handleCelebrationComplete}
        autoDismissMs={2500}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
      {/* Main modal container - full screen on mobile, centered card on desktop */}
      <div className="relative w-full h-full md:max-w-lg md:h-auto md:max-h-[90vh] bg-white rounded-3xl overflow-hidden flex flex-col shadow-2xl">

        {/* Header — clean white with maroon accent */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Check In
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content area - scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Step 1: Location Selection & Info */}
          {step === 'info' && (
            <>
              {/* Hero: Where are you today? */}
              <div className="text-center py-2">
                <p className="text-2xl font-bold text-slate-800">Where are you today?</p>
                <p className="text-sm text-slate-500 mt-1">Select your location to mark attendance</p>
              </div>

              {/* Location Tabs — Home Gym / Other Gyms */}
              {(() => {
                const homeGyms = locations.filter(l => l.is_home_gym !== false);
                const otherGyms = locations.filter(l => l.is_home_gym === false);
                const activeList = locationTab === 'home' ? homeGyms : otherGyms;
                const renderCard = (loc, idx) => {
                  const isSelected = selectedLocation?.location_id === loc.location_id;
                  const isHQ = loc.location_type === 'hq';
                  const isHome = loc.is_home_gym !== false;
                  return (
                    <button
                      key={loc.location_id || idx}
                      onClick={() => setSelectedLocation(isSelected ? null : loc)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-[#7C1D3E] bg-[#7C1D3E]/5 shadow-md shadow-[#7C1D3E]/10'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'bg-[#7C1D3E] text-white'
                            : isHQ ? 'bg-blue-50 text-blue-600'
                            : isHome ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold leading-tight ${isSelected ? 'text-[#7C1D3E]' : 'text-slate-800'}`}>
                            {loc.location_name || loc.name}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {isHQ ? 'Headquarters' : isHome ? 'Home Gym' : 'Visiting'}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-[#7C1D3E] flex items-center justify-center flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                };
                return (
                  <>
                    <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
                      <button
                        onClick={() => setLocationTab('home')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                          locationTab === 'home'
                            ? 'bg-white text-[#7C1D3E] shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Home Gym ({homeGyms.length})
                      </button>
                      <button
                        onClick={() => setLocationTab('other')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                          locationTab === 'other'
                            ? 'bg-white text-[#7C1D3E] shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        Other Gyms ({otherGyms.length})
                      </button>
                    </div>
                    <div className="space-y-2">
                      {locationsLoading ? (
                        <div className="flex items-center justify-center gap-2 text-slate-400 py-8">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Loading locations...</span>
                        </div>
                      ) : activeList.length === 0 ? (
                        <div className="text-center py-6 text-slate-400 text-sm">No gyms in this category</div>
                      ) : (
                        activeList.map(renderCard)
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Time + Status — compact row */}
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">{formatTimeIST(currentTime)}</span>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                  isFullDay
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {isFullDay ? 'Full Day' : 'Half Day'}
                </span>
              </div>

              {/* GPS status — visible so user can see signal strength */}
              <GPSIndicator
                targetLat={selectedLocation?.latitude || selectedLocation?.lat}
                targetLon={selectedLocation?.longitude || selectedLocation?.lon}
                geoFenceRadius={selectedLocation?.geofence_radius || 150}
                gymName={selectedLocation?.location_name || selectedLocation?.name}
                onLocationUpdate={handleLocationUpdate}
                showRetryButton={true}
              />

              {gpsTimedOut && !gpsLocation && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>GPS is taking longer than usual. Move near a window or step outside for better signal.</span>
                </div>
              )}

              {/* GPS Warning — outside geofence, check-in will be rejected by server */}
              {selectedLocation && gpsLocation && (gpsWarning || distanceWarning) && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>You appear to be outside the gym zone. Move closer to the gym entrance before checking in.</span>
                </div>
              )}

              {/* Continue Button — only needs location selected, GPS is background */}
              <Button
                onClick={proceedToCamera}
                disabled={!selectedLocation || !gpsLocation}
                className={`w-full h-14 text-lg font-semibold rounded-2xl text-white shadow-lg transition-all ${
                  selectedLocation
                    ? 'bg-[#7C1D3E] hover:bg-[#5C0E2E] shadow-[#7C1D3E]/30 hover:shadow-[#7C1D3E]/50'
                    : 'bg-slate-300 cursor-not-allowed'
                }`}
              >
                {!selectedLocation ? (
                  <>
                    <MapPin className="w-5 h-5 mr-2 opacity-50" />
                    Select Your Location
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5 mr-2" />
                    Continue — Take Photo
                  </>
                )}
              </Button>
            </>
          )}

          {/* Step 2: Camera */}
          {step === 'camera' && (
            <>
              {/* Normal camera view or fallback UI */}
              {!photoError ? (
                <>
                  <div className="camera-frame aspect-[3/4] bg-black relative">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      screenshotQuality={0.9}
                      videoConstraints={videoConstraints}
                      className="w-full h-full object-cover"
                      mirrored={facingMode === 'user'}
                      onUserMediaError={handleWebcamError}
                    />

                    {/* Camera overlay with corner brackets */}
                    <CameraOverlay />

                    {/* Countdown overlay */}
                    {showCountdown && (
                      <CountdownTimer
                        duration={3}
                        onComplete={handleCountdownComplete}
                      />
                    )}

                    {/* Camera toggle button */}
                    <button
                      onClick={toggleCamera}
                      className="absolute top-4 right-4 p-3 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors z-10"
                    >
                      <SwitchCamera className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Photo quality indicator */}
                  <PhotoQualityIndicator
                    webcamRef={webcamRef}
                    isActive={step === 'camera' && !showCountdown}
                    className="bg-slate-100/80 rounded-xl px-4 py-2 justify-center"
                  />

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setStep('info')}
                      className="flex-1 h-12 rounded-xl"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={startCapture}
                      disabled={showCountdown}
                      className="flex-1 h-12 rounded-xl gradient-maroon text-white"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                </>
              ) : (
                /* Photo Fallback UI (EDGE-03) */
                <div className="space-y-4">
                  {/* Phone camera fallback - first option */}
                  {fallbackMode === 'phone' && (
                    <div className="text-center p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                      <Smartphone className="h-12 w-12 mx-auto text-blue-600 mb-3" />
                      <h3 className="font-semibold text-slate-800 mb-2">Camera not available</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Please use your phone's camera app to take a selfie, then upload it here.
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload">
                        <Button asChild className="gradient-maroon text-white">
                          <span>
                            <Camera className="w-4 h-4 mr-2" />
                            Take Photo with Phone
                          </span>
                        </Button>
                      </label>
                      <Button
                        variant="link"
                        onClick={() => setFallbackMode('text')}
                        className="mt-3 text-slate-500"
                      >
                        Camera still not working?
                      </Button>
                    </div>
                  )}

                  {/* Text reason fallback - last resort */}
                  {fallbackMode === 'text' && (
                    <div className="p-4 border rounded-xl bg-amber-50 border-amber-200">
                      <div className="flex items-center gap-2 text-amber-800 mb-3">
                        <FileText className="h-5 w-5" />
                        <h3 className="font-semibold">Enter reason for no photo</h3>
                      </div>
                      <textarea
                        value={fallbackReason}
                        onChange={(e) => setFallbackReason(e.target.value)}
                        placeholder="Explain why you cannot take a photo (e.g., camera broken, device issue)..."
                        className="w-full p-3 border border-amber-300 rounded-lg min-h-[100px] bg-white text-slate-800"
                      />
                      <p className="text-xs text-amber-600 mt-2">
                        This will be flagged for manager review and approval.
                      </p>
                      <div className="flex gap-3 mt-4">
                        <Button
                          variant="outline"
                          onClick={() => setFallbackMode('phone')}
                          className="flex-1"
                        >
                          Back
                        </Button>
                        <Button
                          onClick={handleTextFallbackSubmit}
                          disabled={!fallbackReason.trim() || loading}
                          className="flex-1 gradient-maroon text-white"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            'Submit Without Photo'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Back button for fallback modes */}
                  {fallbackMode && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPhotoError(false);
                        setFallbackMode(null);
                        setStep('info');
                      }}
                      className="w-full"
                    >
                      Back to Location
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && capturedImage && (
            <>
              {/* GPS Warning Banner in preview (EDGE-02) */}
              {(gpsWarning || distanceWarning) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium">Review Required</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    {gpsWarning?.message || distanceWarning?.message}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Your check-in will be flagged for manager review.
                  </p>
                </div>
              )}

              <div className="camera-frame aspect-[3/4] bg-black relative">
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={retake}
                  disabled={retakeCount >= MAX_RETAKES || loading}
                  className="flex-1 h-12 rounded-xl"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retake {retakeCount < MAX_RETAKES && `(${MAX_RETAKES - retakeCount})`}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-12 rounded-xl gradient-maroon text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking in...
                    </>
                  ) : (
                    'Submit Check-In'
                  )}
                </Button>
              </div>

              {submitError && (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <RefreshCw size={16} />
                  Retry
                </Button>
              )}

              {/* GPS confirmation with warning indicator */}
              {gpsLocation && (
                <div className={`text-center text-sm ${gpsWarning ? 'text-amber-600' : 'text-slate-500'}`}>
                  Location: {gpsLocation.accuracy.toFixed(0)}m accuracy
                  {gpsWarning && ' (flagged)'}
                </div>
              )}
            </>
          )}

          {/* Step 4: Submitting */}
          {step === 'submitting' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              {loading && (
                <div className="w-full space-y-2">
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-[#800000] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
              <p className="text-slate-600 font-medium">Processing your check-in...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
